import { Button } from "@designs-by-maryam/ui/components/button";
import { Input } from "@designs-by-maryam/ui/components/input";
import { Skeleton } from "@designs-by-maryam/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	Briefcase,
	Clock,
	FileText,
	FolderOpen,
	Search,
	User,
} from "lucide-react";
import { useState } from "react";
import { requireClient } from "@/functions/require-role";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/_dashboard/portal/")({
	component: PortalDashboard,
	beforeLoad: async () => {
		// Server-side role check - no client-side flash
		await requireClient();
	},
});

type WorkflowStatus =
	| "planning"
	| "design"
	| "approval"
	| "implementation"
	| "completed";

const STATUS_CONFIG: Record<
	WorkflowStatus,
	{ label: string; color: string; bgColor: string }
> = {
	planning: {
		label: "Planning",
		color: "text-gray-700 dark:text-gray-300",
		bgColor: "bg-gray-100 dark:bg-gray-800",
	},
	design: {
		label: "Design",
		color: "text-blue-700 dark:text-blue-300",
		bgColor: "bg-blue-100 dark:bg-blue-900/50",
	},
	approval: {
		label: "Approval",
		color: "text-yellow-700 dark:text-yellow-300",
		bgColor: "bg-yellow-100 dark:bg-yellow-900/50",
	},
	implementation: {
		label: "Implementation",
		color: "text-purple-700 dark:text-purple-300",
		bgColor: "bg-purple-100 dark:bg-purple-900/50",
	},
	completed: {
		label: "Completed",
		color: "text-green-700 dark:text-green-300",
		bgColor: "bg-green-100 dark:bg-green-900/50",
	},
};

function PortalDashboard() {
	const [filter, setFilter] = useState<"all" | "active" | "completed">("all");
	const [searchQuery, setSearchQuery] = useState("");

	const { data: projects, isLoading } = useQuery(
		orpc.portalGetMyProjects.queryOptions(),
	);

	const { data: profile } = useQuery(orpc.portalGetMyProfile.queryOptions());

	// Filter projects
	const filteredProjects = projects?.filter((project) => {
		const matchesFilter =
			filter === "all" ||
			(filter === "active" && project.workflowStatus !== "completed") ||
			(filter === "completed" && project.workflowStatus === "completed");

		const matchesSearch =
			!searchQuery ||
			project.title.toLowerCase().includes(searchQuery.toLowerCase());

		return matchesFilter && matchesSearch;
	});

	return (
		<div className="container mx-auto px-4 py-8 md:px-6">
			{/* Header */}
			<div className="mb-8">
				<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div>
						<h1 className="font-bold text-2xl md:text-3xl">
							Welcome back
							{profile?.contactName
								? `, ${profile.contactName.split(" ")[0]}`
								: ""}
						</h1>
						<p className="mt-1 text-muted-foreground">
							Track your projects and view documents
						</p>
					</div>
					<div className="flex gap-3">
						<Link to="/portal/invoices">
							<Button variant="outline">
								<FileText className="mr-2 h-4 w-4" />
								View Invoices
							</Button>
						</Link>
					</div>
				</div>
			</div>

			{/* Quick Stats */}
			<div className="mb-8 grid gap-4 md:grid-cols-4">
				<StatCard
					icon={<Briefcase className="h-5 w-5" />}
					isLoading={isLoading}
					label="Total Projects"
					value={projects?.length || 0}
				/>
				<StatCard
					icon={<Clock className="h-5 w-5" />}
					isLoading={isLoading}
					label="In Progress"
					value={
						projects?.filter((p) => p.workflowStatus !== "completed").length ||
						0
					}
				/>
				<StatCard
					icon={<FolderOpen className="h-5 w-5" />}
					isLoading={isLoading}
					label="Completed"
					value={
						projects?.filter((p) => p.workflowStatus === "completed").length ||
						0
					}
				/>
				<StatCard
					icon={<User className="h-5 w-5" />}
					isLoading={isLoading}
					label="Your Role"
					value={profile?.status === "active" ? "Active Client" : "Client"}
				/>
			</div>

			{/* Filters */}
			<div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
				<div className="flex gap-2">
					{(["all", "active", "completed"] as const).map((f) => (
						<Button
							aria-pressed={filter === f}
							key={f}
							onClick={() => setFilter(f)}
							size="sm"
							variant={filter === f ? "default" : "outline"}
						>
							{f.charAt(0).toUpperCase() + f.slice(1)}
						</Button>
					))}
				</div>
				<div className="relative w-full md:w-64">
					<Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 text-muted-foreground" />
					<Input
						className="pl-9"
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search projects..."
						value={searchQuery}
					/>
				</div>
			</div>

			{/* Projects Grid */}
			<ProjectsGrid
				filteredProjects={filteredProjects}
				isLoading={isLoading}
				searchQuery={searchQuery}
			/>
		</div>
	);
}

type ProjectCardProps = {
	project: {
		id: string;
		projectId: string | null;
		title: string;
		description?: string | null;
		category?: string | null;
		thumbnail?: string | null;
		workflowStatus: string;
		progressPercentage: number;
		nextAction?: string | null;
		createdAt: string;
		updatedAt: string;
	};
};

function ProjectCard({ project }: ProjectCardProps) {
	const status =
		STATUS_CONFIG[project.workflowStatus as WorkflowStatus] ||
		STATUS_CONFIG.planning;
	const thumbnail = project.thumbnail ? (
		// biome-ignore lint/a11y/noNoninteractiveElementInteractions: onError handles image fallback.
		<img
			alt={project.title}
			className="h-full w-full object-cover transition-transform group-hover:scale-105"
			height={180}
			loading="lazy"
			onError={(e) => {
				e.currentTarget.style.display = "none";
			}}
			src={project.thumbnail}
			width={320}
		/>
	) : (
		<div className="flex h-full items-center justify-center">
			<FolderOpen className="h-12 w-12 text-muted-foreground/50" />
		</div>
	);

	return (
		<Link
			className="group block rounded-xl border bg-card transition-all hover:border-primary/50 hover:shadow-lg"
			params={{ id: project.id }}
			to="/portal/projects/$id"
		>
			{/* Thumbnail */}
			<div className="relative aspect-video overflow-hidden rounded-t-xl bg-muted">
				{thumbnail}
				{/* Status Badge */}
				<div
					className={`absolute top-3 right-3 rounded-full px-3 py-1 font-medium text-xs ${status.bgColor} ${status.color}`}
				>
					{status.label}
				</div>
			</div>

			{/* Content */}
			<div className="p-4">
				<h3 className="font-semibold text-lg group-hover:text-primary">
					{project.title}
				</h3>
				{project.description && (
					<p className="mt-1 line-clamp-2 text-muted-foreground text-sm">
						{project.description}
					</p>
				)}

				{/* Progress Bar */}
				<div className="mt-4">
					<div className="mb-1 flex items-center justify-between text-xs">
						<span className="text-muted-foreground">Progress</span>
						<span className="font-medium">{project.progressPercentage}%</span>
					</div>
					<div className="h-2 overflow-hidden rounded-full bg-muted">
						<div
							className="h-full rounded-full bg-primary transition-all"
							style={{ width: `${project.progressPercentage}%` }}
						/>
					</div>
				</div>

				{/* Next Action */}
				{project.nextAction && (
					<div className="mt-3 rounded-lg bg-muted/50 p-2">
						<p className="text-muted-foreground text-xs">
							Next: {project.nextAction}
						</p>
					</div>
				)}

				{/* Footer */}
				<div className="mt-4 flex items-center justify-between text-muted-foreground text-xs">
					<span>
						Updated {new Date(project.updatedAt).toLocaleDateString()}
					</span>
					<span className="flex items-center gap-1 text-primary group-hover:underline">
						View Details <ArrowRight className="h-3 w-3" />
					</span>
				</div>
			</div>
		</Link>
	);
}

type StatCardProps = {
	icon: React.ReactNode;
	label: string;
	value: number | string;
	isLoading?: boolean;
};

function StatCard({ icon, label, value, isLoading }: StatCardProps) {
	return (
		<div className="rounded-lg border bg-card p-4">
			<div className="flex items-center gap-3">
				<div className="rounded-full bg-primary/10 p-2 text-primary dark:bg-primary/20 dark:text-primary">
					{icon}
				</div>
				<div>
					<p className="text-muted-foreground text-sm">{label}</p>
					{isLoading ? (
						<Skeleton className="mt-1 h-6 w-12" />
					) : (
						<p className="font-bold text-xl">{value}</p>
					)}
				</div>
			</div>
		</div>
	);
}

function ProjectCardSkeleton() {
	return (
		<div className="rounded-xl border bg-card">
			<Skeleton className="aspect-video rounded-t-xl" />
			<div className="p-4">
				<Skeleton className="h-6 w-3/4" />
				<Skeleton className="mt-2 h-4 w-full" />
				<Skeleton className="mt-4 h-2 w-full" />
				<Skeleton className="mt-4 h-4 w-1/2" />
			</div>
		</div>
	);
}

type ProjectsGridProps = {
	isLoading: boolean;
	filteredProjects: ProjectCardProps["project"][] | undefined;
	searchQuery: string;
};

function ProjectsGrid({
	isLoading,
	filteredProjects,
	searchQuery,
}: ProjectsGridProps) {
	if (isLoading) {
		return (
			<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
				{[1, 2, 3].map((n) => (
					<ProjectCardSkeleton key={`skeleton-${n}`} />
				))}
			</div>
		);
	}

	if (filteredProjects && filteredProjects.length > 0) {
		return (
			<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
				{filteredProjects.map((project) => (
					<ProjectCard key={project.id} project={project} />
				))}
			</div>
		);
	}

	return (
		<div className="rounded-lg border bg-card p-12 text-center">
			<FolderOpen className="mx-auto h-12 w-12 text-muted-foreground" />
			<h3 className="mt-4 font-semibold text-lg">No projects found</h3>
			<p className="mt-2 text-muted-foreground">
				{searchQuery
					? "Try adjusting your search terms"
					: "Your projects will appear here once they're started"}
			</p>
		</div>
	);
}
